import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import './NFT.css';

// ============== CONFIGURATION ==============
const CHAIN_ID = 143n;
const RPC_URL = 'https://rpc.monad.xyz';
const NFT_CONTRACT = '0xEB036Af9fC62DCda581dF142C6bf7433ace7BF6F';
const GHOST_TOKEN = '0x601CE35D2aD556599F796323c98b25a8d5D17777';
const MINT_PRICE = ethers.parseUnits('100000', 18);
const MAX_SUPPLY = 1000;

// ============== ABIs ==============
const NFT_ABI = [
  "function mint() external",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)"
];

const ERC20_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)"
];

// ============== ICONS ==============
const GhostLogo = () => <img src="ghost.png" alt="Ghost Protocol" width="40" height="40" style={{ borderRadius: '8px' }} />;
const MonadLogo = () => <img src="monad-logo.png" alt="Monad" width="20" height="20" style={{ borderRadius: '4px' }} />;
const XLogo = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
const TelegramIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>;

function NFT({ onBack, onNavigate }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [totalMinted, setTotalMinted] = useState(0);
  const [ghostBalance, setGhostBalance] = useState('0');
  const [allowance, setAllowance] = useState(0n);
  const [mintedNFT, setMintedNFT] = useState(null);
  const [userNFTs, setUserNFTs] = useState([]);
  const [showMintPopup, setShowMintPopup] = useState(false);
  const [newlyMintedNFT, setNewlyMintedNFT] = useState(null);

  const connectWallet = async () => {
    if (!window.ethereum) {
      setMessage('❌ Please install MetaMask');
      return;
    }
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (BigInt(chainId) !== CHAIN_ID) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x8F' }]
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0x8F',
                chainName: 'Monad Mainnet',
                nativeCurrency: { name: 'MON', symbol: 'MON', decimals: 18 },
                rpcUrls: [RPC_URL],
                blockExplorerUrls: ['https://monadvision.com']
              }]
            });
          }
        }
      }
      setAccount(accounts[0]);
      setMessage('✅ Wallet connected!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Failed to connect wallet');
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      const nftContract = new ethers.Contract(NFT_CONTRACT, NFT_ABI, provider);
      const supply = await nftContract.totalSupply();
      setTotalMinted(Number(supply));
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchNFTImage = useCallback(async (tokenId) => {
    try {
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      const nftContract = new ethers.Contract(NFT_CONTRACT, NFT_ABI, provider);
      const tokenURI = await nftContract.tokenURI(tokenId);
      
      const jsonBase64 = tokenURI.replace('data:application/json;base64,', '');
      const jsonString = atob(jsonBase64);
      const metadata = JSON.parse(jsonString);
      
      return {
        tokenId: tokenId,
        image: metadata.image,
        name: metadata.name,
        attributes: metadata.attributes
      };
    } catch (error) {
      console.error('Error fetching NFT image:', error);
      return null;
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!account) return;
    try {
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      const ghostContract = new ethers.Contract(GHOST_TOKEN, ERC20_ABI, provider);
      const nftContract = new ethers.Contract(NFT_CONTRACT, NFT_ABI, provider);
      
      const balance = await ghostContract.balanceOf(account);
      setGhostBalance(ethers.formatUnits(balance, 18));
      const allow = await ghostContract.allowance(account, NFT_CONTRACT);
      setAllowance(allow);

      try {
        const nftBalance = await nftContract.balanceOf(account);
        const nfts = [];
        for (let i = 0; i < Number(nftBalance); i++) {
          const tokenId = await nftContract.tokenOfOwnerByIndex(account, i);
          nfts.push(Number(tokenId));
        }
        setUserNFTs(nfts);

        if (nfts.length > 0 && !mintedNFT) {
          const latestTokenId = nfts[nfts.length - 1];
          const nftData = await fetchNFTImage(latestTokenId);
          if (nftData) setMintedNFT(nftData);
        }
      } catch (e) {
        console.error('Error fetching user NFTs:', e);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, [account, mintedNFT, fetchNFTImage]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        setAccount(accounts[0] || null);
        setMintedNFT(null);
        setUserNFTs([]);
      });
      window.ethereum.on('chainChanged', () => window.location.reload());
    }
  }, []);

  const handleApprove = async () => {
    if (!account) return;
    setLoading(true);
    setMessage('');
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const ghostContract = new ethers.Contract(GHOST_TOKEN, ERC20_ABI, signer);
      setMessage('⏳ Approving GHOST tokens...');
      const tx = await ghostContract.approve(NFT_CONTRACT, ethers.MaxUint256);
      await tx.wait();
      setAllowance(ethers.MaxUint256);
      setMessage('✅ GHOST approved! You can now mint.');
    } catch (error) {
      console.error('Approval error:', error);
      setMessage('❌ Approval failed: ' + (error.reason || error.message));
    }
    setLoading(false);
  };

  const handleMint = async () => {
    if (!account) return;
    setLoading(true);
    setMessage('');
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const nftContract = new ethers.Contract(NFT_CONTRACT, NFT_ABI, signer);
      setMessage('⏳ Minting your Ghost PFP...');
      const tx = await nftContract.mint();
      const receipt = await tx.wait();
      
      let tokenId = null;
      for (const log of receipt.logs) {
        try {
          const parsed = nftContract.interface.parseLog(log);
          if (parsed.name === 'Transfer') {
            tokenId = Number(parsed.args[2]);
            break;
          }
        } catch { }
      }
      
      if (tokenId !== null) {
        setMessage(`✅ Minted Ghost #${tokenId}! 🎉`);
        const nftData = await fetchNFTImage(tokenId);
        if (nftData) {
          setMintedNFT(nftData);
          setNewlyMintedNFT(nftData);
          setShowMintPopup(true);
        }
        setUserNFTs(prev => [...prev, tokenId]);
      } else {
        setMessage('✅ Minted successfully! 🎉');
      }
      
      fetchStats();
      fetchUserData();
    } catch (error) {
      console.error('Mint error:', error);
      if (error.message?.includes('insufficient')) {
        setMessage('❌ Insufficient GHOST balance. Need 100,000 GHOST.');
      } else if (error.message?.includes('Max supply')) {
        setMessage('❌ All NFTs have been minted!');
      } else {
        setMessage('❌ Mint failed: ' + (error.reason || error.message));
      }
    }
    setLoading(false);
  };

  const handleSelectNFT = async (tokenId) => {
    const nftData = await fetchNFTImage(tokenId);
    if (nftData) setMintedNFT(nftData);
  };

  const shareOnTwitter = () => {
    const tweetText = `👻 Just minted Ghost #${newlyMintedNFT?.tokenId}!

Only ${MAX_SUPPLY - totalMinted} left. Burn 100k $Ghost to mint yours before they're gone 🔥

Mint here: ghost-protocol.xyz

@monad`;
    
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(tweetUrl, '_blank');
  };

  const closePopup = () => {
    setShowMintPopup(false);
    setNewlyMintedNFT(null);
  };

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    } else if (onBack) {
      onBack(page);
    }
  };

  const needsApproval = allowance < MINT_PRICE;
  const remaining = MAX_SUPPLY - totalMinted;
  const isSoldOut = remaining <= 0;
  const totalBurned = totalMinted * 100000;
  const progress = (totalMinted / MAX_SUPPLY) * 100;

  return (
    <div className="nft-page">
      {/* Mint Success Popup */}
      {showMintPopup && newlyMintedNFT && (
        <div className="mint-popup-overlay" onClick={closePopup}>
          <div className="mint-popup" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={closePopup}>×</button>
            <div className="popup-content">
              <h2>🎉 Congratulations!</h2>
              <p className="popup-subtitle">You just minted a Ghost PFP!</p>
              
              <div className="popup-nft-image">
                <img src={newlyMintedNFT.image} alt={newlyMintedNFT.name} />
              </div>
              
              <h3>{newlyMintedNFT.name}</h3>
              
              {newlyMintedNFT.attributes && (
                <div className="popup-traits">
                  {newlyMintedNFT.attributes.map((attr, idx) => (
                    <div key={idx} className="popup-trait">
                      <span className="trait-type">{attr.trait_type}</span>
                      <span className="trait-value">{attr.value}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <button className="twitter-share-btn" onClick={shareOnTwitter}>
                <XLogo />
                Share on X
              </button>
              
              <button className="popup-done-btn" onClick={closePopup}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="nft-header">
        <div className="header-left">
          <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('mixer'); }} className="logo">
            <GhostLogo />
            <span>Ghost Protocol</span>
          </a>
          <nav className="nav">
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('mixer'); }}>Mixer</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('docs'); }}>Docs</a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleNavigation('voting'); }}>Voting</a>
            <a href="#" className="active">NFT</a>
          </nav>
        </div>
        <div className="header-right">
          <div className="network-badge"><MonadLogo /><span>Monad</span></div>
          {account ? (
            <div className="wallet-pill">{account.slice(0, 6)}...{account.slice(-4)}</div>
          ) : (
            <button className="connect-btn" onClick={connectWallet}>Connect Wallet</button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="nft-main">
        <div className="nft-container">
          {/* Left Side - Preview */}
          <div className="preview-side">
            <div className="preview-card">
              {mintedNFT ? (
                <div className="minted-nft-display">
                  <img src={mintedNFT.image} alt={mintedNFT.name} className="nft-image" />
                </div>
              ) : (
                <div className="ghost-wrapper">
                  <div className="ghost-emoji">👻</div>
                </div>
              )}
            </div>
            <div className="preview-label">
              {mintedNFT ? mintedNFT.name : 'Your unique Ghost awaits'}
            </div>
            
            {/* User's NFT thumbnails */}
            {userNFTs.length > 1 && (
              <div className="user-nfts-row">
                {userNFTs.map(tokenId => (
                  <div 
                    key={tokenId} 
                    className={`nft-thumb ${mintedNFT?.tokenId === tokenId ? 'active' : ''}`}
                    onClick={() => handleSelectNFT(tokenId)}
                  >
                    #{tokenId}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Mint Interface */}
          <div className="mint-side">
            <div className="mint-card">
              <h1>Ghost NFT Collection</h1>
              <p className="description">Burn GHOST tokens to mint unique on-chain generative ghosts. Each NFT is 100% on-chain with randomized traits.</p>
              
              {/* Stats Grid */}
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-number">{totalMinted}<span className="stat-total">/{MAX_SUPPLY}</span></div>
                  <div className="stat-label">Minted</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number green">{remaining}</div>
                  <div className="stat-label">Remaining</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number orange">{totalBurned.toLocaleString()}</div>
                  <div className="stat-label">🔥 Burned</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="progress-section">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="progress-text">{progress.toFixed(1)}% minted</div>
              </div>

              {/* Price */}
              <div className="price-box">
                <span className="price-label">Mint Price</span>
                <span className="price-value">100,000 GHOST</span>
              </div>

              {/* User Balance */}
              {account && (
                <div className="balance-box">
                  <span>Your Balance:</span>
                  <span className="balance-amount">{parseFloat(ghostBalance).toLocaleString()} GHOST</span>
                </div>
              )}

              {/* Mint Button */}
              {!account ? (
                <button className="mint-btn" onClick={connectWallet}>
                  Connect Wallet to Mint
                </button>
              ) : isSoldOut ? (
                <button className="mint-btn disabled" disabled>
                  🎉 Sold Out!
                </button>
              ) : needsApproval ? (
                <button className="mint-btn approve" onClick={handleApprove} disabled={loading}>
                  {loading ? '⏳ Approving...' : '1️⃣ Approve GHOST'}
                </button>
              ) : (
                <button className="mint-btn" onClick={handleMint} disabled={loading}>
                  {loading ? '⏳ Minting...' : '🔥 Mint Ghost PFP'}
                </button>
              )}

              {/* Message */}
              {message && (
                <div className={`msg ${message.includes('✅') ? 'success' : message.includes('❌') ? 'error' : ''}`}>
                  {message}
                </div>
              )}

              {/* Burn Notice */}
              <div className="burn-notice">
                🔥 100,000 GHOST permanently burned per mint
              </div>

              {/* Contract Link */}
              <a 
                href={`https://monadvision.com/address/${NFT_CONTRACT}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="contract-link"
              >
                View Contract on MonadVision ↗
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="nft-footer">
        <div className="footer-left">
          <span className="token-ca">$GHOST: {GHOST_TOKEN}</span>
        </div>
        <div className="footer-right">
          <a href="https://x.com/GhostProtocolHQ" target="_blank" rel="noopener noreferrer"><XLogo /></a>
          <a href="https://t.me/GhostProtocolHQ" target="_blank" rel="noopener noreferrer"><TelegramIcon /></a>
        </div>
      </footer>
    </div>
  );
}

export default NFT;
